import type { AppConfig } from "../../types/appConfig";
import type {
  Equipment,
  EquipmentModel as EquipmentModelType,
  EquipmentModelsPaginationRequest,
  EquipmentSearchRequest,
} from "./Equipment.typed";
import type { PaginatedResponse } from "../zekay/pagination";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";

export class EquipmentModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async getEquipmentModels(
    paginationParams?: EquipmentModelsPaginationRequest,
  ): Promise<PaginatedResponse<EquipmentModelType> | EquipmentModelType[]> {
    const params = new URLSearchParams();
    if (paginationParams) {
      if (paginationParams.page !== undefined)
        params.append("page", paginationParams.page.toString());
      if (paginationParams.limit !== undefined)
        params.append("limit", paginationParams.limit.toString());
    }
    const url = `${this.zekayAPI}/equipment/models${params.toString() ? "?" + params.toString() : ""}`;
    const r = await fetchZekay(url);
    await expectStatuses(r, 200, 204);
    if (r.status === 204) {
      return paginationParams ? { data: [], total: 0, page: 1, limit: 50, total_pages: 0 } : [];
    }
    const result = await r.json();
    if (
      paginationParams &&
      (paginationParams.page !== undefined || paginationParams.limit !== undefined)
    ) {
      return result as PaginatedResponse<EquipmentModelType>;
    }
    return Array.isArray(result) ? result : result.data || [];
  }

  async getEquipmentModel(id: number): Promise<EquipmentModelType> {
    const r = await fetchZekay(`${this.zekayAPI}/equipment/models/${id}`);
    await assertResponseOk(r);
    return (await r.json()) as EquipmentModelType;
  }

  async createEquipmentModel(model: EquipmentModelType): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/equipment/models", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(model),
    });
    await expectStatuses(response, 200, 204);
  }

  async updateEquipmentModel(model: EquipmentModelType): Promise<void> {
    if (!model.ID) throw new Error("ID du modèle manquant");
    const response = await fetchZekay(`${this.zekayAPI}/equipment/models/${model.ID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(model),
    });
    await expectStatuses(response, 204);
  }

  async deleteEquipmentModel(id: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/equipment/models/${id}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }

  async getEquipments(): Promise<Equipment[]> {
    const r = await fetchZekay(this.zekayAPI + "/equipment");
    await expectStatuses(r, 200, 204);
    if (r.status === 204) return [];
    return (await r.json()) as Equipment[];
  }

  async getEquipment(id: string): Promise<Equipment> {
    const r = await fetchZekay(`${this.zekayAPI}/equipment/${id}`);
    await assertResponseOk(r);
    return (await r.json()) as Equipment;
  }

  async createEquipment(equipment: Equipment): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/equipment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(equipment),
    });
    await expectStatuses(response, 204);
  }

  async updateEquipment(equipment: Equipment): Promise<void> {
    if (!equipment.ID) throw new Error("ID du matériel manquant");
    const response = await fetchZekay(`${this.zekayAPI}/equipment/${equipment.ID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(equipment),
    });
    await expectStatuses(response, 204);
  }

  async deleteEquipment(id: string): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/equipment/${id}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }

  async getEquipmentQRCode(id: string): Promise<Blob> {
    const r = await fetchZekay(`${this.zekayAPI}/equipment/${id}/qrcode`);
    await assertResponseOk(r);
    return r.blob();
  }

  async regenerateEquipmentQRCode(id: string, backgroundColor?: string): Promise<void> {
    const body = backgroundColor ? { BackgroundColor: backgroundColor } : {};
    const response = await fetchZekay(`${this.zekayAPI}/equipment/${id}/qrcode/regenerate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await expectStatuses(response, 204);
  }

  async getEquipmentsByModelID(
    modelId: number,
    page?: number,
    limit?: number,
  ): Promise<{ equipments: Equipment[]; total: number; pages: number }> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append("page", page.toString());
    if (limit !== undefined) params.append("limit", limit.toString());
    const url = `${this.zekayAPI}/equipment/model/${modelId}${params.toString() ? "?" + params.toString() : ""}`;
    const r = await fetchZekay(url);
    await expectStatuses(r, 200, 204, 404);
    if (r.status === 404 || r.status === 204) {
      return { equipments: [], total: 0, pages: 0 };
    }
    const result = await r.json();
    if (Array.isArray(result)) {
      return { equipments: result, total: result.length, pages: 1 };
    }
    const data = result as { data?: Equipment[]; total?: number; total_pages?: number };
    return {
      equipments: Array.isArray(data.data) ? data.data : [],
      total: Number(data.total ?? 0),
      pages: Number(data.total_pages ?? 0),
    };
  }

  async searchEquipmentsByModelID(
    searchRequest: EquipmentSearchRequest,
  ): Promise<{ equipments: Equipment[]; total: number; pages: number }> {
    const r = await fetchZekay(`${this.zekayAPI}/equipment/search`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(searchRequest),
    });
    await expectStatuses(r, 200, 204, 404);
    if (r.status === 404 || r.status === 204) {
      return { equipments: [], total: 0, pages: 0 };
    }
    const result = await r.json();
    const data = result as { data?: Equipment[]; total?: number; total_pages?: number };
    return {
      equipments: Array.isArray(data.data) ? data.data : [],
      total: Number(data.total ?? 0),
      pages: Number(data.total_pages ?? 0),
    };
  }

  async exportEquipmentsByModelIDCSV(modelId: number): Promise<Blob> {
    const r = await fetchZekay(`${this.zekayAPI}/equipment/model/${modelId}/export/csv`);
    await assertResponseOk(r);

    return r.blob();
  }

  async searchEquipmentsBatch(
    requests: EquipmentSearchRequest[],
  ): Promise<Array<{ total: number }>> {
    if (requests.length === 0) return [];
    if (requests.length > 20) throw new Error("Lot limité à 20 recherches maximum.");
    const r = await fetchZekay(`${this.zekayAPI}/equipment/search/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requests),
    });
    await assertResponseOk(r);
    const raw = (await r.json()) as Array<{ total?: number; Total?: number }>;
    return raw.map((item) => {
      const t = item.total ?? item.Total;
      const n = Number(t);
      return { total: Number.isFinite(n) ? n : 0 };
    });
  }
}
